import React, { useEffect, useRef, useState } from 'react';
import { palette, radius, spacing, typography, zIndex } from '../../../style-utils/theme';
import { Btn, ErrorBox, NoteBox } from './admin.styled';

/**
 * THE STEP-UP, AND THE TYPED CONFIRMATION.
 *
 * One component for both because they are one interaction: an operator is about
 * to do something to somebody else's account, and the console has to be sure
 * (a) it is still them at the keyboard and (b) they meant this specific thing.
 *
 * ── WHY A TYPED CONFIRMATION AND NOT AN "ARE YOU SURE" ─────────────────────
 *
 * "Are you sure?" is answered by muscle memory. Typing the name of the
 * organisation you are about to suspend is not, and it is the difference
 * between suspending the customer you meant and the one whose row was under the
 * cursor. Reserved for the acts that reach outside our own systems — suspending
 * an organisation, deactivating a person, granting this console to somebody.
 *
 * ── AND WHY THE REASON IS REQUIRED HERE TOO ────────────────────────────────
 *
 * The server refuses anything shorter than eight characters (400
 * REASON_REQUIRED), and the client asks for it up front rather than letting the
 * request bounce: the reason lands in the platform audit trail AND in the
 * customer's own audit_log, so it is the sentence somebody reads months later
 * when they ask why their account was touched. A field that can only be
 * discovered by failing is a field people learn to fill with "x".
 */

export interface ConfirmSpec {
  title: string;
  /** What is about to happen, in one plain sentence. */
  body: React.ReactNode;
  /** The exact string the operator must type. Omit for a non-destructive act. */
  typeToConfirm?: string;
  confirmLabel: string;
  danger?: boolean;
  /** Called with the reason once everything is satisfied. */
  onConfirm: (reason: string) => Promise<void>;
}

const Backdrop: React.FC<{ children: React.ReactNode; onClose: () => void }> = ({
  children,
  onClose,
}) => (
  <div
    role="presentation"
    onMouseDown={e => {
      if (e.target === e.currentTarget) onClose();
    }}
    style={{
      position: 'fixed',
      inset: 0,
      background: palette.overlay,
      zIndex: zIndex.modal,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.sm,
    }}
  >
    {children}
  </div>
);

const Panel: React.FC<{ children: React.ReactNode; labelledBy: string }> = ({
  children,
  labelledBy,
}) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby={labelledBy}
    style={{
      width: 'min(520px, 100%)',
      maxHeight: '90vh',
      overflowY: 'auto',
      background: palette.surface,
      border: `1px solid ${palette.line}`,
      borderRadius: radius.md,
      padding: spacing.md,
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.sm,
    }}
  >
    {children}
  </div>
);

const field: React.CSSProperties = {
  width: '100%',
  height: 38,
  padding: `0 ${spacing.xs}`,
  border: `1px solid ${palette.lineStrong}`,
  borderRadius: radius.xs,
  fontFamily: typography.ui,
  /* 16px so iOS does not zoom the page in on focus and leave it there. */
  fontSize: 16,
  boxSizing: 'border-box',
};

/**
 * The password prompt. Shown when a sensitive action needs a live step-up.
 *
 * ── `onClose` IS TOLD WHETHER THE STEP-UP SUCCEEDED (2026-09-16) ───────────
 *
 * It used to be a bare `onClose()`, and the shell decided what had happened by
 * reading `elevation.elevated` from its own state. That state had not been
 * re-rendered yet at the moment this dialog closed, so a SUCCESSFUL step-up
 * looked exactly like a cancelled one: the shell dropped the held intent and
 * the confirmation never opened. Every first sensitive action in a fresh
 * ten-minute window silently needed a second click — the precise thing the
 * shell's own comment says holding the intent is there to prevent.
 *
 * Found by driving the console as an operator (e2e-audit/promos.spec.ts
 * Journey A), because no API test can see a dialog that does not open.
 */
export const ElevateDialog: React.FC<{
  onElevate: (password: string) => Promise<true | string>;
  onClose: (elevated: boolean) => void;
  busy?: boolean;
}> = ({ onElevate, onClose, busy }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(false);
    };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const result = await onElevate(password);
    if (result === true) onClose(true);
    else setError(result);
  };

  return (
    <Backdrop onClose={() => onClose(false)}>
      <Panel labelledBy="elevate-title">
        <h2 id="elevate-title" style={{ margin: 0, fontSize: typography.size.md }}>
          Confirm it is you
        </h2>
        <p style={{ margin: 0, color: palette.slate, fontSize: typography.size.xs }}>
          Actions that change a customer&apos;s account need your password again. The
          confirmation lasts ten minutes and is tied to this browser session.
        </p>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <label htmlFor="elevate-password" style={{ fontSize: typography.size.xs }}>
            Password
          </label>
          <input
            id="elevate-password"
            ref={input}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={field}
          />
          {error ? <ErrorBox role="alert">{error}</ErrorBox> : null}
          <div style={{ display: 'flex', gap: spacing.xs, justifyContent: 'flex-end' }}>
            <Btn type="button" onClick={() => onClose(false)}>
              Cancel
            </Btn>
            <Btn type="submit" variant="primary" disabled={busy || !password}>
              {busy ? 'Checking…' : 'Confirm'}
            </Btn>
          </div>
        </form>
      </Panel>
    </Backdrop>
  );
};

/** The action confirmation: reason, optional typed name, and the verdict. */
export const ConfirmDialog: React.FC<{ spec: ConfirmSpec; onClose: () => void }> = ({
  spec,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    first.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const reasonOk = reason.trim().length >= 8;
  const typedOk = !spec.typeToConfirm || typed.trim() === spec.typeToConfirm;

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await spec.onConfirm(reason.trim());
      onClose();
    } catch (e) {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      const msg = (e as any)?.response?.data?.message || (e as Error).message;
      setError(msg || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Backdrop onClose={busy ? () => undefined : onClose}>
      <Panel labelledBy="confirm-title">
        <h2 id="confirm-title" style={{ margin: 0, fontSize: typography.size.md }}>
          {spec.title}
        </h2>
        <div style={{ fontSize: typography.size.xs, color: palette.slate }}>{spec.body}</div>

        <NoteBox>
          This is recorded in the platform audit trail and in the customer&apos;s own
          activity log, with your name and the reason you give below.
        </NoteBox>

        <label htmlFor="confirm-reason" style={{ fontSize: typography.size.xs }}>
          Reason <span style={{ color: palette.slateLight }}>(at least 8 characters)</span>
        </label>
        <textarea
          id="confirm-reason"
          ref={first}
          rows={2}
          value={reason}
          onChange={e => setReason(e.target.value)}
          style={{ ...field, height: 'auto', padding: spacing.xs, resize: 'vertical' }}
        />

        {spec.typeToConfirm ? (
          <>
            <label htmlFor="confirm-typed" style={{ fontSize: typography.size.xs }}>
              Type <strong>{spec.typeToConfirm}</strong> to confirm
            </label>
            <input
              id="confirm-typed"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              style={field}
              autoComplete="off"
              spellCheck={false}
            />
          </>
        ) : null}

        {error ? <ErrorBox role="alert">{error}</ErrorBox> : null}

        <div style={{ display: 'flex', gap: spacing.xs, justifyContent: 'flex-end' }}>
          <Btn type="button" onClick={onClose} disabled={busy}>
            Cancel
          </Btn>
          <Btn
            type="button"
            variant={spec.danger ? 'danger' : 'primary'}
            onClick={go}
            disabled={busy || !reasonOk || !typedOk}
          >
            {busy ? 'Working…' : spec.confirmLabel}
          </Btn>
        </div>
      </Panel>
    </Backdrop>
  );
};
